import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useReducer,
    useState,
} from 'react';
import { FilterGroupDefn, FilterID, FilterGroupID } from '../index';

export type FilterChangeAction = 'add' | 'remove' | 'clear' | 'replace';
export type FilterChangeHandler = (payload: {
    action: FilterChangeAction;
    filterID?: FilterID;
    listAfter: FilterID[];
}) => void;

type FilterContextType = {
    filterGroups: FilterGroupDefn[];
    searchValue: string | undefined;
    appliedFilters: FilterID[];
    addFilterGroup: (filterGroup: FilterGroupDefn) => void;
    removeFilterGroup: (filterGroupID: FilterGroupID) => void;
    clearFilterGroups: () => void;
    replaceFilterGroups: (filterGroups: FilterGroupDefn[]) => void;
    setSearchValue: Dispatch<SetStateAction<string | undefined>>;
    clearSearchValue: () => void;
    addAppliedFilter: (filterID: FilterID) => void;
    removeAppliedFilter: (filterID: FilterID) => void;
    clearAppliedFilters: () => void;
    replaceAppliedFilters: (filters: FilterID[]) => void;
    afterFilterChange: FilterChangeHandler;
    setAfterFilterChangeHandler: (
        afterFilterChange: FilterChangeHandler
    ) => void;
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

type FilterGroupAction =
    | {
          type: 'add';
          payload: FilterGroupDefn;
      }
    | {
          type: 'remove';
          payload: FilterGroupID;
      }
    | {
          type: 'clear';
          payload?: never;
      }
    | {
          type: 'replace';
          payload: FilterGroupDefn[];
      };

function filterGroupReducer(
    state: FilterGroupDefn[],
    { type, payload }: FilterGroupAction
) {
    switch (type) {
        case 'add':
            return [...state, payload];
        case 'remove':
            return state.filter(({ key }) => key !== payload);
        case 'clear':
            return [];
        case 'replace':
            return payload;
        default:
            throw new Error(
                `Trying to use an illegal action "${type}" on useFilter's filterGroupReducer`
            );
    }
}

type AppliedFiltersAction =
    | {
          type: 'add';
          payload: FilterID;
          afterChangeHandler?: FilterChangeHandler;
      }
    | {
          type: 'remove';
          payload: FilterID;
          afterChangeHandler?: FilterChangeHandler;
      }
    | {
          type: 'clear';
          payload?: never;
          afterChangeHandler?: FilterChangeHandler;
      }
    | {
          type: 'replace';
          payload: FilterID[];
          afterChangeHandler?: FilterChangeHandler;
      };

function appliedFiltersReducer(
    state: FilterID[],
    { type, payload, afterChangeHandler }: AppliedFiltersAction
) {
    switch (type) {
        case 'add': {
            const listAfter = [...new Set([...state, payload])];
            afterChangeHandler?.({
                action: 'add',
                filterID: payload,
                listAfter: listAfter,
            });
            return listAfter;
        }
        case 'remove': {
            const listAfter = [...state].filter(
                (filterID) => filterID !== payload
            );
            afterChangeHandler?.({
                action: 'remove',
                filterID: payload,
                listAfter: listAfter,
            });
            return listAfter;
        }
        case 'clear': {
            const listAfter: FilterID[] = [];
            afterChangeHandler?.({
                action: 'clear',
                listAfter: listAfter,
            });
            return listAfter;
        }
        case 'replace': {
            const listAfter = [...payload];
            afterChangeHandler?.({
                action: 'replace',
                listAfter: listAfter,
            });
            return listAfter;
        }
        default: {
            throw new Error(
                `Trying to use an illegal "${type}" action on useFilter's appliedFiltersReducer`
            );
        }
    }
}

export const FilterProvider = ({ children }: { children: ReactNode }) => {
    const [filterGroups, dispatchFilterGroupAction] = useReducer(
        filterGroupReducer,
        []
    );
    const [searchValue, setSearchValue] = useState<string | undefined>(
        undefined
    );
    const [appliedFilters, dispatchAppliedFiltersAction] = useReducer(
        appliedFiltersReducer,
        []
    );

    const addFilterGroup = useCallback(
        (filterGroup: FilterGroupDefn) => {
            dispatchFilterGroupAction({ type: 'add', payload: filterGroup });
        },
        [dispatchFilterGroupAction]
    );

    const removeFilterGroup = useCallback(
        (filterGroupID: FilterGroupID) => {
            dispatchFilterGroupAction({
                type: 'remove',
                payload: filterGroupID,
            });
        },
        [dispatchFilterGroupAction]
    );

    const clearFilterGroups = useCallback(() => {
        dispatchFilterGroupAction({ type: 'clear' });
    }, [dispatchFilterGroupAction]);

    const replaceFilterGroups = useCallback(
        (filterGroups: FilterGroupDefn[]) => {
            dispatchFilterGroupAction({
                type: 'replace',
                payload: filterGroups,
            });
        },
        [dispatchFilterGroupAction]
    );

    const clearSearchValue = useCallback(() => {
        setSearchValue(undefined);
    }, [setSearchValue]);

    const [afterFilterChange, setAfterFilterChangeHandlerState] =
        useState<FilterChangeHandler>(() => void 0);

    const addAppliedFilter = useCallback(
        (filterID: FilterID) => {
            dispatchAppliedFiltersAction({
                type: 'add',
                payload: filterID,
                afterChangeHandler: afterFilterChange,
            });
        },
        [dispatchAppliedFiltersAction, afterFilterChange]
    );

    const removeAppliedFilter = useCallback(
        (filterID: FilterID) => {
            dispatchAppliedFiltersAction({
                type: 'remove',
                payload: filterID,
                afterChangeHandler: afterFilterChange,
            });
        },
        [dispatchAppliedFiltersAction, afterFilterChange]
    );

    const clearAppliedFilters = useCallback(() => {
        dispatchAppliedFiltersAction({
            type: 'clear',
            afterChangeHandler: afterFilterChange,
        });
    }, [dispatchAppliedFiltersAction, afterFilterChange]);

    const replaceAppliedFilters = useCallback(
        (filters: FilterID[]) => {
            dispatchAppliedFiltersAction({
                type: 'replace',
                payload: filters,
                afterChangeHandler: afterFilterChange,
            });
        },
        [dispatchAppliedFiltersAction, afterFilterChange]
    );

    return (
        <FilterContext.Provider
            value={{
                filterGroups,
                searchValue,
                appliedFilters,
                addFilterGroup,
                removeFilterGroup,
                clearFilterGroups,
                replaceFilterGroups,
                setSearchValue,
                clearSearchValue,
                addAppliedFilter,
                removeAppliedFilter,
                clearAppliedFilters,
                replaceAppliedFilters,
                afterFilterChange: afterFilterChange,
                setAfterFilterChangeHandler: (afterFilterChange) =>
                    setAfterFilterChangeHandlerState(() => afterFilterChange),
            }}
        >
            {children}
        </FilterContext.Provider>
    );
};

export const useFilter = () => {
    const context = useContext(FilterContext);
    if (context === undefined) {
        throw new Error('useFilter must be used within a FilterProvider');
    }
    return context;
};
